import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { collection, addDoc, serverTimestamp, query, where, getDocs, getDoc, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import {
  getHolesByCourse,
  getCourseNames,
  getCourseHandicap,
  getStrokesForHole,
  calculateStablefordPoints,
  getBergvlietTeeboxes,
  getBergvlietCourseHandicap
} from './courseData';
import { getWeatherForCourse } from './weatherService';
import { createNotification } from './notifications';
import { computeFinaleLeaderboard, formatFinaleScore, formatTodayScore, getScoreColor } from './finaleUtils';

function ScoreEntry({ onClose }) {
  const [step, setStep] = useState(1);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [handicap, setHandicap] = useState('');
  const [flightMembers, setFlightMembers] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState('');
  const [currentHoleIndex, setCurrentHoleIndex] = useState(0);
  const [holeData, setHoleData] = useState({});
  const [subStep, setSubStep] = useState('strokes');
  const [cigarettesSmoked, setCigarettesSmoked] = useState('');
  const [cigarettesBorrowed, setCigarettesBorrowed] = useState('');
  const [loading, setLoading] = useState(false);
  const [beersAfterRound, setBeersAfterRound] = useState(null);
  const [submittedRoundId, setSubmittedRoundId] = useState(null);
  const [savedRoundData, setSavedRoundData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', message: '', type: 'info' });
  const [approvedMembers, setApprovedMembers] = useState([]);
  const [currentUserName, setCurrentUserName] = useState('');
  const [availableEvents, setAvailableEvents] = useState([]);
  const [activeSeason, setActiveSeason] = useState(null);
  const [showAbortConfirm, setShowAbortConfirm] = useState(false);
  const [selectedTeebox, setSelectedTeebox] = useState('');
  const [finaleBonus, setFinaleBonus] = useState(0);
  const [finaleRoundDocId, setFinaleRoundDocId] = useState(null);
  const [showFinaleLeaderboard, setShowFinaleLeaderboard] = useState(false);
  const [finaleLeaderboardData, setFinaleLeaderboardData] = useState([]);
  const [finaleLeaderboardLoading, setFinaleLeaderboardLoading] = useState(false);
  const [finaleSyncErrorShown, setFinaleSyncErrorShown] = useState(false);

  const holes = selectedCourse ? getHolesByCourse(selectedCourse) : [];
  const currentHole = holes[currentHoleIndex];
  const isFinale18 = selectedEvent?.type === 'finale_18';
  const courseHandicap = handicap
    ? (isFinale18
        ? getBergvlietCourseHandicap(
            parseFloat(handicap),
            selectedEvent.finaleConfig?.teeboxMode === 'admin'
              ? selectedEvent.finaleConfig?.adminTeebox
              : selectedTeebox
          )
        : getCourseHandicap(parseFloat(handicap), selectedCourse))
    : 0;

  // Load active season and available events
  useEffect(() => {
    const loadEventsAndSeason = async () => {
      try {
        const seasonsQuery = query(collection(db, 'seasons'), where('isActive', '==', true));
        const seasonsSnapshot = await getDocs(seasonsQuery);
        
        if (seasonsSnapshot.empty) {
          showMessage('No Active Season', 'There is no active season. Please contact an admin.', 'error');
          return;
        }
        
        const season = { id: seasonsSnapshot.docs[0].id, ...seasonsSnapshot.docs[0].data() };
        setActiveSeason(season);
        
        const eventsQuery = query(
          collection(db, 'events'), 
          where('seasonId', '==', season.id)
        );
        
        const unsubscribe = onSnapshot(eventsQuery, async (snapshot) => {
          const events = await Promise.all(
            snapshot.docs.map(async (eventDoc) => {
              const eventData = { id: eventDoc.id, ...eventDoc.data() };
              
              const roundsQuery = query(
                collection(db, 'rounds'),
                where('eventId', '==', eventData.id),
                where('userId', '==', auth.currentUser.uid)
              );
              const roundsSnapshot = await getDocs(roundsQuery);
              eventData.alreadySubmitted = !roundsSnapshot.empty;
              
              return eventData;
            })
          );
          
          // Filter out events more than 6 days in the past
const today = new Date();
const sixDaysAgo = new Date();
sixDaysAgo.setDate(today.getDate() - 6);

const filteredEvents = events.filter(event => {
  const eventDate = new Date(event.date);
  return eventDate >= sixDaysAgo; // Only show events from last 6 days or future
});

const sortedEvents = filteredEvents.sort((a, b) => new Date(a.date) - new Date(b.date));

setAvailableEvents(sortedEvents);
        });
        
        return unsubscribe;
      } catch (error) {
        console.error('Error loading events:', error);
        showMessage('Error', 'Failed to load events. Please try again.', 'error');
      }
    };

    loadEventsAndSeason();
  }, []);

  // Load approved members
  useEffect(() => {
    const loadApprovedMembers = async () => {
      try {
        const q = query(collection(db, 'users'), where('status', '==', 'approved'));
        const snapshot = await getDocs(q);
        const members = snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(member => member.id !== auth.currentUser.uid)
          .sort((a, b) => a.name.localeCompare(b.name));
        setApprovedMembers(members);
      } catch (error) {
        console.error('Error loading members:', error);
      }
    };

    loadApprovedMembers();
  }, []);

  // Load the current user's display name from their Firestore profile.
  // auth.currentUser.displayName is never set for email/password accounts,
  // so falling back to it (or the email) shows the player's email address instead of their name.
  useEffect(() => {
    const loadCurrentUserName = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        if (userDoc.exists()) {
          setCurrentUserName(userDoc.data().name || '');
        }
      } catch (error) {
        console.error('Error loading current user profile:', error);
      }
    };

    loadCurrentUserName();
  }, []);

  // Save progress to localStorage
  useEffect(() => {
    if (selectedEvent && selectedCourse && handicap && step !== 'resume' && step !== 10) {
      const progressData = {
        userId: auth.currentUser?.uid,
        selectedEvent,
        selectedCourse,
        handicap,
        flightMembers,
        selectedTeebox,
        currentHoleIndex,
        holeData,
        subStep,
        step,
        finaleRoundDocId,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('golfRoundInProgress', JSON.stringify(progressData));
    }
  }, [selectedEvent, selectedCourse, handicap, flightMembers, selectedTeebox, currentHoleIndex, holeData, subStep, step, finaleRoundDocId]);

  // Load saved progress on mount — only if it belongs to the currently logged-in user.
  // Without this check, switching accounts on the same device/browser would resume the
  // previous player's in-progress round (and its finaleRoundDocId), silently overwriting
  // their finale leaderboard entry instead of creating a new one for the new player.
  useEffect(() => {
    const saved = localStorage.getItem('golfRoundInProgress');
    if (saved) {
      const data = JSON.parse(saved);
      if (data.userId && data.userId !== auth.currentUser?.uid) {
        localStorage.removeItem('golfRoundInProgress');
        return;
      }
      const savedTime = new Date(data.timestamp);
      const now = new Date();
      const hoursDiff = (now - savedTime) / (1000 * 60 * 60);

      if (hoursDiff < 24) {
        setSavedRoundData(data);
        setStep('resume');
      }
    }
  }, []);

  // Look up this player's finale bonus strokes once they reach the round summary.
  // Bonus assignments are locked in at event-creation time (based on regular season
  // standings as of that date) and stored on the event — never recomputed live, so
  // they don't shift as other players' rounds get approved during the finale.
  useEffect(() => {
    if (step !== 8 || selectedEvent?.type !== 'finale_18' || !activeSeason) return;

    const bonusAssignments = selectedEvent.finaleConfig?.bonusAssignments || {};
    setFinaleBonus(bonusAssignments[auth.currentUser.uid] || 0);
  }, [step, selectedEvent, activeSeason]);

  // Reset the live finale round doc id when this component unmounts
  useEffect(() => {
    return () => {
      setFinaleRoundDocId(null);
    };
  }, []);

  const loadFinaleLeaderboard = async () => {
    if (!selectedEvent) return;
    setFinaleLeaderboardLoading(true);
    try {
      const finaleRoundsSnap = await getDocs(query(
        collection(db, 'finaleRounds'),
        where('eventId', '==', selectedEvent.id)
      ));
      const finaleRounds = finaleRoundsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      const data = await computeFinaleLeaderboard({
        todayFinaleEvent: selectedEvent,
        activeSeason,
        finaleRounds,
      });
      setFinaleLeaderboardData(data);
    } catch (error) {
      console.error('Error loading finale leaderboard:', error);
    } finally {
      setFinaleLeaderboardLoading(false);
    }
  };

  // Fetch finale leaderboard data whenever the leaderboard modal is opened
  useEffect(() => {
    if (showFinaleLeaderboard) {
      loadFinaleLeaderboard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFinaleLeaderboard]);

  const showMessage = (title, message, type = 'info') => {
    setModalContent({ title, message, type });
    setShowModal(true);
  };

  const deleteFinaleRoundDoc = async (finaleDocId) => {
    if (!finaleDocId) return;
    try {
      await deleteDoc(doc(db, 'finaleRounds', finaleDocId));
    } catch (error) {
      console.error('Error deleting finale round doc:', error);
    }
  };

  const handleResumeRound = () => {
    setSelectedEvent(savedRoundData.selectedEvent);
    setSelectedCourse(savedRoundData.selectedCourse);
    setHandicap(savedRoundData.handicap);
    setFlightMembers(savedRoundData.flightMembers || []);
    setSelectedTeebox(savedRoundData.selectedTeebox || '');
    setCurrentHoleIndex(savedRoundData.currentHoleIndex);
    setHoleData(savedRoundData.holeData);
    setSubStep(savedRoundData.subStep || 'strokes');
    setStep(savedRoundData.step || 3);
    setFinaleRoundDocId(savedRoundData.finaleRoundDocId || null);
    setSavedRoundData(null);
  };

  const handleStartNewRound = () => {
    deleteFinaleRoundDoc(savedRoundData?.finaleRoundDocId);
    localStorage.removeItem('golfRoundInProgress');
    setSavedRoundData(null);
    setFinaleRoundDocId(null);
    setStep(1);
  };

  const handleEventSelect = (event) => {
    if (event.alreadySubmitted) {
      showMessage(
        'Already Submitted', 
        'You have already submitted a round for this event. You can only submit one round per event.',
        'error'
      );
      return;
    }
    
    setSelectedEvent(event);
    if (event.type === 'finale_18') {
      setSelectedCourse('Bergvliet');
      setStep(2);
    } else {
      setStep(1.5);
    }
  };

  const handleCourseSelect = (course) => {
    setSelectedCourse(course);
    setStep(2);
  };

  const handleHandicapSubmit = () => {
    if (!handicap || handicap < -4 || handicap > 54) {
      showMessage('Invalid Handicap', 'Please enter a valid handicap between -4 and 54', 'error');
      return;
    }
    if (selectedEvent.type === 'finale_18') {
      if (selectedEvent.finaleConfig?.teeboxMode === 'player') {
        setStep(2.2);
      } else {
        setSelectedTeebox(selectedEvent.finaleConfig?.adminTeebox || '');
        setStep(2.5);
      }
      return;
    }
    setStep(2.5);
  };

  const handleTeeboxSelect = (color) => {
    setSelectedTeebox(color);
    setStep(2.5);
  };

  const handleFlightMemberToggle = (memberId) => {
    if (flightMembers.includes(memberId)) {
      setFlightMembers(flightMembers.filter(id => id !== memberId));
    } else {
      if (flightMembers.length < 3) {
        setFlightMembers([...flightMembers, memberId]);
      }
    }
  };

  const handleFlightMembersSubmit = async () => {
    if (flightMembers.length === 0) {
      showMessage('Select Flight Members', 'Please select at least 1 person you played with', 'error');
      return;
    }
    setStep(3);
    setCurrentHoleIndex(0);
    setSubStep('strokes');

    if (selectedEvent?.type === 'finale_18') {
      try {
        const combinedHandicap = courseHandicap + finaleBonus;
        const docRef = await addDoc(collection(db, 'finaleRounds'), {
          userId: auth.currentUser.uid,
          eventId: selectedEvent.id,
          seasonId: activeSeason.id,
          course: 'Bergvliet',
          teeboxColor: selectedTeebox,
          courseHandicap: courseHandicap,
          finaleBonus: finaleBonus,
          combinedHandicap: combinedHandicap,
          status: 'in_progress',
          holeData: {},
          holesCompleted: 0,
          totalGross: 0,
          totalHandicapStrokes: 0,
          finaleNetGross: 0,
          playerName: currentUserName || auth.currentUser.displayName || auth.currentUser.email,
          startedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        setFinaleRoundDocId(docRef.id);
      } catch (error) {
        console.error('Error creating finale round doc:', error);
        showMessage(
          'Live Leaderboard Sync Failed',
          `We couldn't add you to the live finale leaderboard (your scores will still save normally). Error: ${error.code || error.message || 'unknown'}`,
          'error'
        );
      }
    }
  };

  const handleHoleDataChange = (field, value) => {
    const holeNumber = currentHole.number;
    setHoleData({
      ...holeData,
      [holeNumber]: {
        ...holeData[holeNumber],
        [field]: value
      }
    });
  };

  const getCurrentHoleData = () => {
    return holeData[currentHole.number] || {};
  };

  const handleSubStepNext = (field, value) => {
  handleHoleDataChange(field, value);
  
  if (subStep === 'strokes') {
    setSubStep('putts');
  }
};

  const handlePreviousHole = () => {
    if (currentHoleIndex > 0) {
      setCurrentHoleIndex(currentHoleIndex - 1);
      setSubStep('strokes');
    }
  };

  const updateFinaleRoundProgress = async (latestHoleData) => {
    if (!finaleRoundDocId) return;
    try {
      let runningGross = 0;
      let runningHandicapStrokes = 0;
      let completedCount = 0;
      const updatedHoleData = { ...latestHoleData };

      holes.forEach(hole => {
        const data = updatedHoleData[hole.number];
        if (!data || !data.strokes) return;

        const strokesReceived = getStrokesForHole(hole.strokeIndex, courseHandicap, selectedCourse);
        const points = calculateStablefordPoints(data.strokes, hole.par, strokesReceived);

        updatedHoleData[hole.number] = {
          ...data,
          stablefordPoints: points,
          grossVsPar: data.strokes - hole.par
        };

        runningGross += data.strokes;
        runningHandicapStrokes += strokesReceived;
        completedCount++;
      });

      await updateDoc(doc(db, 'finaleRounds', finaleRoundDocId), {
        holeData: updatedHoleData,
        holesCompleted: completedCount,
        totalGross: runningGross,
        totalHandicapStrokes: runningHandicapStrokes,
        finaleNetGross: runningGross - runningHandicapStrokes - finaleBonus,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating finale round progress:', error);
      if (!finaleSyncErrorShown) {
        setFinaleSyncErrorShown(true);
        showMessage(
          'Live Leaderboard Sync Failed',
          `Your hole scores aren't reaching the live finale leaderboard (your round progress is still saved locally). Error: ${error.code || error.message || 'unknown'}`,
          'error'
        );
      }
    }
  };

  const moveToNextHole = (latestHoleData = holeData) => {
    if (isFinale18 && finaleRoundDocId) {
      updateFinaleRoundProgress(latestHoleData);
    }
    if (currentHoleIndex < holes.length - 1) {
      setCurrentHoleIndex(currentHoleIndex + 1);
      setSubStep('strokes');
    } else {
      setStep(8);
    }
  };

  const handleSaveAndExit = () => {
    showMessage(
      '💾 Progress Saved!', 
      'Your round has been saved. You can continue later from where you left off.',
      'success'
    );
    setTimeout(() => {
      onClose();
    }, 1500);
  };

  const calculateRunningTotals = () => {
    let totalGross = 0;
    let totalStableford = 0;
    let holesCompleted = 0;

    holes.forEach((hole, index) => {
      const data = holeData[hole.number];
      if (data && data.strokes > 0 && index < currentHoleIndex) {
        totalGross += data.strokes;
        const strokes = getStrokesForHole(hole.strokeIndex, courseHandicap, selectedCourse);
        const points = calculateStablefordPoints(data.strokes, hole.par, strokes);
        totalStableford += points;
        holesCompleted++;
      }
    });

    const currentData = getCurrentHoleData();
    if (currentData.strokes > 0) {
      totalGross += currentData.strokes;
      const strokes = getStrokesForHole(currentHole.strokeIndex, courseHandicap, selectedCourse);
      const points = calculateStablefordPoints(currentData.strokes, currentHole.par, strokes);
      totalStableford += points;
      holesCompleted++;
    }

    return { totalGross, totalStableford, holesCompleted };
  };

 const calculateFinalTotals = () => {
    let totalGross = 0;
    let totalStableford = 0;
    let totalPutts = 0;
    let fairwaysHit = 0;
    let fairwaysTotal = 0;
    let greensInRegulation = 0;
    let upAndDowns = 0;
    let upAndDownOpportunities = 0;
    let totalHandicapStrokes = 0;

    holes.forEach(hole => {
      const data = holeData[hole.number];
      if (data && data.strokes > 0) {
        totalGross += data.strokes;

        const strokes = getStrokesForHole(hole.strokeIndex, courseHandicap, selectedCourse);
        totalHandicapStrokes += strokes;
        const points = calculateStablefordPoints(data.strokes, hole.par, strokes);
        totalStableford += points;

        if (data.putts) totalPutts += parseInt(data.putts);
        
        // Fairway calculation
        if (hole.par >= 4) {
          fairwaysTotal++;
          if (data.fairwayHit === 'yes') fairwaysHit++;
        }

        // Auto-calculate GIR
        const shotsBeforePutts = data.strokes - (data.putts || 0);
        const girTarget = hole.par - 2;
        let hitGIR = false;
        
        if (data.greenInRegulation === 'yes') {
          hitGIR = true;
        } else if (data.greenInRegulation === 'no') {
          hitGIR = false;
        } else {
          // Auto-calculate if not manually set
          hitGIR = shotsBeforePutts <= girTarget;
        }

        if (hitGIR) {
          greensInRegulation++;
        } else {
          upAndDownOpportunities++;
          
          if (data.strokes <= hole.par) {
            upAndDowns++;
          }
        }
      }
    });

    return {
      totalGross,
      totalStableford,
      totalPutts,
      fairwaysHit,
      fairwaysTotal,
      fairwayPercentage: fairwaysTotal > 0 ? Math.round((fairwaysHit / fairwaysTotal) * 100) : 0,
      greensInRegulation,
      girPercentage: Math.round((greensInRegulation / holes.length) * 100),
      upAndDowns,
      upAndDownOpportunities,
      upAndDownPercentage: upAndDownOpportunities > 0 ? Math.round((upAndDowns / upAndDownOpportunities) * 100) : 0,
      totalHandicapStrokes
    };
  };

  const handleMarkerSelect = (markerId) => {
    setSelectedMarker(markerId);
  };

  const handleMarkerSubmit = () => {
    if (!selectedMarker) {
      showMessage('Select Marker', 'Please select who will verify your scorecard', 'error');
      return;
    }
    setStep(9);
  };

  const handleFinalSubmit = async () => {
    if (!cigarettesSmoked || !cigarettesBorrowed) {
      showMessage('Missing Information', 'Please answer the required cigarette questions', 'error');
      return;
    }

    setLoading(true);

    try {
      const totals = calculateFinalTotals();
      
      const roundData = {
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email,
        seasonId: activeSeason.id,
        eventId: selectedEvent.id,
        eventName: selectedEvent.name,
        course: selectedCourse,
        handicap: parseFloat(handicap),
        courseHandicap: courseHandicap,
        flightMembers: flightMembers,
        markerId: selectedMarker,
        status: 'pending',
        holeData: holeData,
        totalGross: totals.totalGross,
        totalStableford: totals.totalStableford,
        totalPutts: totals.totalPutts,
        fairwaysHit: totals.fairwaysHit,
        fairwaysTotal: totals.fairwaysTotal,
        fairwayPercentage: totals.fairwayPercentage,
        greensInRegulation: totals.greensInRegulation,
        girPercentage: totals.girPercentage,
        upAndDowns: totals.upAndDowns,
        upAndDownOpportunities: totals.upAndDownOpportunities,
        upAndDownPercentage: totals.upAndDownPercentage,
        cigarettesSmoked: parseInt(cigarettesSmoked),
        cigarettesBorrowed: parseInt(cigarettesBorrowed),
        timestamp: serverTimestamp(),
        date: new Date().toISOString()
      };

      if (selectedEvent.type === 'finale_18') {
        roundData.isFinale = true;
        roundData.teeboxColor = selectedTeebox || selectedEvent.finaleConfig?.adminTeebox || '';
        roundData.finaleBonus = finaleBonus;
        roundData.finaleNetGross = totals.totalGross - totals.totalHandicapStrokes - finaleBonus;
      }

      // Fetch weather data
      const weather = await getWeatherForCourse(selectedCourse);
      roundData.weather = weather || null;

      const docRef = await addDoc(collection(db, 'rounds'), roundData);
      setSubmittedRoundId(docRef.id);

      if (selectedEvent.type === 'finale_18' && finaleRoundDocId) {
        try {
          await updateDoc(doc(db, 'finaleRounds', finaleRoundDocId), {
            status: 'completed',
            roundsDocId: docRef.id,
            updatedAt: serverTimestamp(),
          });
        } catch (finaleError) {
          console.error('Error finalizing finale round doc:', finaleError);
        }
      }

      try {
        await createNotification(auth.currentUser.uid, 'round_submitted', { roundId: docRef.id });
      } catch (notifError) {
        console.error('Error sending submission notification:', notifError);
      }
      // Notify the marker
      try {
        const markerName = approvedMembers.find(m => m.id === selectedMarker)?.name || 'Someone';
        await createNotification(selectedMarker, 'round_pending_review', {
          playerName: currentUserName || auth.currentUser.displayName || auth.currentUser.email,
          roundId: docRef.id,
          eventName: selectedEvent.name
        });
      } catch (notifError) {
        console.error('Error sending marker notification:', notifError);
      }

      localStorage.removeItem('golfRoundInProgress');
      localStorage.setItem('pendingBeerEntry', JSON.stringify({ roundId: docRef.id }));
      setStep(10);
    } catch (error) {
      console.error('Error submitting round:', error);
      showMessage('Error', 'There was an error submitting your round. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleBeerSubmit = async (beers) => {
    if (submittedRoundId && beers !== null) {
      try {
        await updateDoc(doc(db, 'rounds', submittedRoundId), {
          beersAfterRound: beers
        });
        localStorage.removeItem('pendingBeerEntry');
      } catch (error) {
        console.error('Error saving beer count:', error);
      }
    }
    // If skipped (beers === null), keep pendingBeerEntry in localStorage
    // so the dashboard banner prompts the user to answer later
    onClose();
  };

  const MessageModal = () => (
    <div style={styles.modalOverlay}>
      <div style={{
        ...styles.modalCard,
        borderTop: `4px solid ${
          modalContent.type === 'success' ? '#48bb78' : 
          modalContent.type === 'error' ? '#e53e3e' : 
          '#3182ce'
        }`
      }}>
        <h3 style={styles.modalTitle}>{modalContent.title}</h3>
        <p style={styles.modalMessage}>{modalContent.message}</p>
        {modalContent.type !== 'success' && (
          <button 
            onClick={() => setShowModal(false)} 
            style={styles.modalButton}
          >
            OK
          </button>
        )}
      </div>
    </div>

  );

  // Group events by category
  const groupEventsByCategory = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const todayEvents = [];
    const upcomingEvents = [];
    const pastEvents = [];
    
    availableEvents.forEach(event => {
      const eventDate = new Date(event.date);
      eventDate.setHours(0, 0, 0, 0);
      
      if (eventDate.getTime() === today.getTime()) {
        todayEvents.push(event);
      } else if (eventDate > today) {
        upcomingEvents.push(event);
      } else {
        pastEvents.push(event);
      }
    });
    
    return { todayEvents, upcomingEvents, pastEvents };
  };

  if (step === 'resume' && savedRoundData) {
    return (
      <div style={styles.overlay}>
        <div style={styles.card}>
          <h2 style={styles.title}>🏌️ Round In Progress</h2>
          <p style={styles.subtitle}>You have an unfinished round</p>

          <div style={styles.resumeInfo}>
            <div style={styles.resumeDetail}>
              <span style={styles.resumeLabel}>Event:</span>
              <span style={styles.resumeValue}>{savedRoundData.selectedEvent?.name}</span>
            </div>
            <div style={styles.resumeDetail}>
              <span style={styles.resumeLabel}>Course:</span>
              <span style={styles.resumeValue}>{savedRoundData.selectedCourse}</span>
            </div>
            <div style={styles.resumeDetail}>
              <span style={styles.resumeLabel}>Progress:</span>
              <span style={styles.resumeValue}>
                Hole {savedRoundData.currentHoleIndex + 1} of {getHolesByCourse(savedRoundData.selectedCourse).length}
              </span>
            </div>
            <div style={styles.resumeDetail}>
              <span style={styles.resumeLabel}>Handicap:</span>
              <span style={styles.resumeValue}>{savedRoundData.handicap}</span>
            </div>
            <div style={styles.resumeDetail}>
              <span style={styles.resumeLabel}>Started:</span>
              <span style={styles.resumeValue}>
                {new Date(savedRoundData.timestamp).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                })}
              </span>
            </div>
          </div>

          <div style={styles.resumeButtons}>
  <button onClick={handleResumeRound} style={styles.resumeButton}>
    📍 Continue Where I Left Off
  </button>
  <button onClick={handleStartNewRound} style={styles.startNewButton}>
    🆕 Start New Round
  </button>
  <button 
    onClick={() => setShowAbortConfirm(true)} 
    style={styles.abortButton}
  >
    🗑️ Delete Saved Round
  </button>
</div>

{showAbortConfirm && (
  <div style={styles.confirmOverlay}>
    <div style={styles.confirmCard}>
      <h3 style={styles.confirmTitle}>🗑️ Delete Saved Round?</h3>
      <p style={styles.confirmText}>
        This will permanently delete your progress on this round. This cannot be undone.
      </p>
      <div style={styles.confirmButtons}>
        <button 
          onClick={() => setShowAbortConfirm(false)} 
          style={styles.confirmCancel}
        >
          Cancel
        </button>
        <button
          onClick={() => {
            deleteFinaleRoundDoc(savedRoundData?.finaleRoundDocId);
            localStorage.removeItem('golfRoundInProgress');
            setShowAbortConfirm(false);
            onClose();
          }}
          style={styles.confirmDelete}
        >
          Delete Round
        </button>
      </div>
    </div>
  </div>
)}

        </div>
      </div>
    );
  }

  if (step === 1) {
    const { todayEvents, upcomingEvents, pastEvents } = groupEventsByCategory();
    
    return (
      <div style={styles.overlay}>
        <div style={styles.card}>
          <h2 style={styles.title}>Select Event</h2>
          <p style={styles.subtitle}>
            {activeSeason ? `${activeSeason.name}` : 'Loading events...'}
          </p>
          
          {availableEvents.length === 0 ? (
            <div style={styles.emptyState}>
              <p>No events available to submit scores for.</p>
              {activeSeason && <p style={{fontSize: '14px', marginTop: '10px'}}>Active Season: {activeSeason.name}</p>}
            </div>
          ) : (
            <div style={styles.eventsContainer}>
              {/* Today's Events */}
              {todayEvents.length > 0 && (
                <div style={styles.eventSection}>
                  <div style={styles.eventSectionHeader}>
                    <span style={styles.todayBadge}>🎯 Today's Game</span>
                    <span style={styles.eventCount}>{todayEvents.length}</span>
                  </div>
                  <div style={styles.eventsList}>
                    {todayEvents.map(event => (
                      <button
                        key={event.id}
                        onClick={() => handleEventSelect(event)}
                        style={{
                          ...styles.eventButton,
                          ...(event.alreadySubmitted ? styles.eventButtonDisabled : {}),
                          ...styles.todayEventButton
                        }}
                        disabled={event.alreadySubmitted}
                      >
                        <div style={styles.eventHeader}>
                          <div style={styles.eventName}>{event.name}</div>
                          {(event.type === 'finale' || event.type === 'finale_18') && (
                            <span style={styles.finaleBadge}>FINALE</span>
                          )}
                          {event.alreadySubmitted && (
                            <span style={styles.submittedBadge}>✓ SUBMITTED</span>
                          )}
                        </div>
                        <div style={styles.eventDetails}>
                          {new Date(event.date).toLocaleDateString('en-US', {
                            weekday: 'long',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                        <div style={styles.eventFormat}>
                          {event.type === 'finale' ? 'Stroke Play' : event.type === 'finale_18' ? 'Stableford + Bonus' : 'Stableford'}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Upcoming Events */}
              {upcomingEvents.length > 0 && (
                <div style={styles.eventSection}>
                  <div style={styles.eventSectionHeader}>
                    <span style={styles.sectionLabel}>📅 Upcoming Games</span>
                    <span style={styles.eventCount}>{upcomingEvents.length}</span>
                  </div>
                  <div style={styles.eventsList}>
                    {upcomingEvents.slice(0, 5).map(event => (
                      <button
                        key={event.id}
                        onClick={() => handleEventSelect(event)}
                        style={{
                          ...styles.eventButton,
                          ...(event.alreadySubmitted ? styles.eventButtonDisabled : {})
                        }}
                        disabled={event.alreadySubmitted}
                      >
                        <div style={styles.eventHeader}>
                          <div style={styles.eventName}>{event.name}</div>
                          {(event.type === 'finale' || event.type === 'finale_18') && (
                            <span style={styles.finaleBadge}>FINALE</span>
                          )}
                          {event.alreadySubmitted && (
                            <span style={styles.submittedBadge}>✓ SUBMITTED</span>
                          )}
                        </div>
                        <div style={styles.eventDetails}>
                          {new Date(event.date).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                        <div style={styles.eventFormat}>
                          {event.type === 'finale' ? 'Stroke Play' : event.type === 'finale_18' ? 'Stableford + Bonus' : 'Stableford'}
                        </div>
                      </button>
                    ))}
                    {upcomingEvents.length > 5 && (
                      <div style={styles.moreEventsText}>
                        + {upcomingEvents.length - 5} more upcoming events
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Past Events */}
              {pastEvents.length > 0 && (
                <div style={styles.eventSection}>
                  <div style={styles.eventSectionHeader}>
                    <span style={styles.sectionLabel}>⏮️ Past Games</span>
                    <span style={styles.eventCount}>{pastEvents.length}</span>
                  </div>
                  <div style={styles.eventsList}>
                    {pastEvents.slice(0, 3).map(event => (
                      <button
                        key={event.id}
                        onClick={() => handleEventSelect(event)}
                        style={{
                          ...styles.eventButton,
                          ...(event.alreadySubmitted ? styles.eventButtonDisabled : {})
                        }}
                        disabled={event.alreadySubmitted}
                      >
                        <div style={styles.eventHeader}>
                          <div style={styles.eventName}>{event.name}</div>
                          {(event.type === 'finale' || event.type === 'finale_18') && (
                            <span style={styles.finaleBadge}>FINALE</span>
                          )}
                          {event.alreadySubmitted && (
                            <span style={styles.submittedBadge}>✓ SUBMITTED</span>
                          )}
                        </div>
                        <div style={styles.eventDetails}>
                          {new Date(event.date).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                        <div style={styles.eventFormat}>
                          {event.type === 'finale' ? 'Stroke Play' : event.type === 'finale_18' ? 'Stableford + Bonus' : 'Stableford'}
                        </div>
                      </button>
                    ))}
                    {pastEvents.length > 3 && (
                      <div style={styles.moreEventsText}>
                        + {pastEvents.length - 3} more past events
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <button onClick={onClose} style={styles.cancelButton}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (step === 1.5) {
    return (
      <div style={styles.overlay}>
        <div style={styles.card}>
          <h2 style={styles.title}>Select Course</h2>
          <p style={styles.subtitle}>{selectedEvent.name}</p>
          
          <div style={styles.courseButtons}>
            {getCourseNames().map(course => (
              <button
                key={course}
                onClick={() => handleCourseSelect(course)}
                style={styles.courseButton}
              >
                <div style={styles.courseName}>{course}</div>
                <div style={styles.courseInfo}>Holes {course === 'De Bleekloop' ? '1-9' : '10-18'}</div>
                <div style={styles.courseInfo}>Par 36</div>
              </button>
            ))}
          </div>

          <div style={styles.buttonGroup}>
            <button onClick={() => setStep(1)} style={styles.backButton}>
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 2) {
    return (
      <div style={styles.overlay}>
        <div style={styles.card}>
          <h2 style={styles.title}>Enter Your Handicap</h2>
          <p style={styles.subtitle}>{selectedEvent.name} • {selectedCourse}</p>
          
          <input
            type="number"
            step="0.1"
            value={handicap}
            onChange={(e) => setHandicap(e.target.value)}
            placeholder="Enter your 18-hole handicap (e.g., 12.5)"
            style={styles.input}
            min="-4"
            max="54"
          />

          <div style={styles.infoBox}>
            Extra Strokes: <strong>{handicap ? courseHandicap : ''}</strong>
          </div>

          <div style={styles.buttonGroup}>
            <button onClick={() => setStep(isFinale18 ? 1 : 1.5)} style={styles.backButton}>
              Back
            </button>
            <button onClick={handleHandicapSubmit} style={styles.nextButton}>
              Next
            </button>
          </div>
        </div>
        {showModal && <MessageModal />}
      </div>
    );
  }

  if (step === 2.2) {
    const teeboxes = getBergvlietTeeboxes();
    return (
      <div style={styles.overlay}>
        <div style={styles.card}>
          <h2 style={styles.title}>Select Your Teebox</h2>
          <p style={styles.subtitle}>{selectedEvent.name} • Bergvliet</p>

          <div style={styles.courseButtons}>
            {Object.entries(teeboxes).map(([color, tb]) => (
              <button
                key={color}
                onClick={() => handleTeeboxSelect(color)}
                style={styles.courseButton}
              >
                <div style={styles.courseName}>{tb.label}</div>
                <div style={styles.courseInfo}>CR {tb.CR} / SR {tb.SR}</div>
              </button>
            ))}
          </div>

          <div style={styles.buttonGroup}>
            <button onClick={() => setStep(2)} style={styles.backButton}>
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 2.5) {
    return (
      <div style={styles.overlay}>
        <div style={styles.card}>
          <h2 style={styles.title}>Who did you play with?</h2>
          <p style={styles.subtitle}>Select 1-3 people in your flight</p>

          <div style={styles.membersList}>
            {approvedMembers.map(member => (
              <button
                key={member.id}
                onClick={() => handleFlightMemberToggle(member.id)}
                style={{
                  ...styles.memberCard,
                  ...(flightMembers.includes(member.id) ? styles.memberCardSelected : {})
                }}
              >
                <div style={styles.memberInfo}>
                  <div style={styles.memberName}>
                    {flightMembers.includes(member.id) && <span style={styles.checkmark}>✓ </span>}
                    {member.name}
                  </div>
                  {member.handicap && (
                    <div style={styles.memberHandicap}>HCP: {member.handicap}</div>
                  )}
                </div>
              </button>
            ))}
          </div>

          <div style={styles.selectionInfo}>
            Selected: {flightMembers.length}/3
          </div>

          <div style={styles.buttonGroup}>
            <button
              onClick={() => setStep(isFinale18 && selectedEvent.finaleConfig?.teeboxMode === 'player' ? 2.2 : 2)}
              style={styles.backButton}
            >
              Back
            </button>
            <button onClick={handleFlightMembersSubmit} style={styles.nextButton}>
              Start Round
            </button>
          </div>
        </div>
        {showModal && <MessageModal />}
      </div>
    );
  }

  if (step === 3 && currentHole) {
    const data = getCurrentHoleData();
    const strokes = getStrokesForHole(currentHole.strokeIndex, courseHandicap, selectedCourse);
    const runningTotals = calculateRunningTotals();

    return (
      <div style={styles.overlay}>
        <div style={styles.card}>
          <div style={styles.totalsBar}>
            <div style={styles.totalItem}>
              <div style={styles.totalLabel}>Points</div>
              <div style={styles.totalValue}>{runningTotals.totalStableford}</div>
            </div>
            <div style={styles.totalItem}>
              <div style={styles.totalLabel}>Strokes</div>
              <div style={styles.totalValue}>{runningTotals.totalGross}</div>
            </div>
            <div style={styles.totalItem}>
              <div style={styles.totalLabel}>Holes</div>
              <div style={styles.totalValue}>{runningTotals.holesCompleted}/{holes.length}</div>
            </div>
          </div>

          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: `${((currentHoleIndex + 1) / holes.length) * 100}%` }} />
          </div>

          <div style={styles.holeHeaderSection}>
            <h2 style={styles.holeTitle}>Hole {currentHole.number}</h2>
            <div style={styles.holeDetails}>
              <span>Par {currentHole.par}</span>
              {currentHole.length && (
                <>
                  <span>•</span>
                  <span>{currentHole.length}m</span>
                </>
              )}
              {strokes !== 0 && (
                <>
                  <span>•</span>
                  <span style={strokes > 0 ? styles.strokesPositive : styles.strokesNegative}>
                    {strokes > 0 ? `+${strokes}` : strokes}
                  </span>
                </>
              )}
            </div>
          </div>

          {subStep === 'strokes' && (
            <div style={styles.questionSection}>
              <h3 style={styles.questionTitle}>How many strokes?</h3>
              <div style={styles.numberPad}>
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(num => (
                  <button
                    key={num}
                    onClick={() => handleSubStepNext('strokes', num)}
                    style={styles.numberButton}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>
          )}

          {subStep === 'putts' && (
  <div style={styles.questionSection}>
    <h3 style={styles.questionTitle}>How many putts?</h3>
    <div style={styles.numberPad}>
      {[0,1,2,3,4,5].map(num => (
        <button
          key={num}
          onClick={() => {
  // Calculate GIR
  const currentData = getCurrentHoleData();
  const shotsBeforePutts = (currentData.strokes || 0) - num;
  const girTarget = currentHole.par - 2;
  const autoGIR = shotsBeforePutts <= girTarget ? 'yes' : 'no';

  // Update both putts AND GIR at the same time
  const holeNumber = currentHole.number;
  const updatedHoleData = {
    ...holeData,
    [holeNumber]: {
      ...holeData[holeNumber],
      putts: num,
      greenInRegulation: autoGIR
    }
  };
  setHoleData(updatedHoleData);

  // Move to next step
  if (currentHole.par >= 4) {
    setSubStep('fairway');
  } else {
    moveToNextHole(updatedHoleData);
  }
}}
          style={styles.numberButton}
        >
          {num}
        </button>
      ))}
    </div>
  </div>
)}

          {subStep === 'fairway' && (
  <div style={styles.questionSection}>
    <h3 style={styles.questionTitle}>Fairway hit?</h3>
    <div style={styles.yesNoButtons}>
      <button
        onClick={() => {
          const holeNumber = currentHole.number;
          const updatedHoleData = {
            ...holeData,
            [holeNumber]: { ...holeData[holeNumber], fairwayHit: 'yes' }
          };
          setHoleData(updatedHoleData);
          moveToNextHole(updatedHoleData);
        }}
        style={styles.yesButton}
      >
        ✓ Yes
      </button>
      <button
        onClick={() => {
          const holeNumber = currentHole.number;
          const updatedHoleData = {
            ...holeData,
            [holeNumber]: { ...holeData[holeNumber], fairwayHit: 'no' }
          };
          setHoleData(updatedHoleData);
          moveToNextHole(updatedHoleData);
        }}
        style={styles.noButton}
      >
        ✗ No
      </button>
    </div>
  </div>
)}

          <div style={styles.holeNavigation}>
            {currentHoleIndex > 0 && (
              <button onClick={handlePreviousHole} style={styles.prevHoleButton}>
                ← Previous Hole
              </button>
            )}
          </div>

          <button onClick={handleSaveAndExit} style={styles.saveExitButton}>
            💾 Save & Exit
          </button>

          {isFinale18 && (
            <button
              onClick={() => setShowFinaleLeaderboard(true)}
              style={styles.finaleLeaderboardButton}
            >
              🏆 Show Finale Leaderboard
            </button>
          )}
        </div>
        {showModal && <MessageModal />}
        {showFinaleLeaderboard && (
          <div style={styles.finaleLeaderboardOverlay}>
            <div style={styles.finaleLeaderboardCard}>
              <div style={styles.finaleLeaderboardHeader}>
                <div>
                  <h3 style={styles.finaleLeaderboardTitle}>🏆 Finale Leaderboard</h3>
                  <p style={styles.finaleLeaderboardSubtitle}>
                    Live standings — {runningTotals.holesCompleted} holes played
                  </p>
                </div>
                <button
                  onClick={() => setShowFinaleLeaderboard(false)}
                  style={styles.finaleLeaderboardClose}
                >
                  ✕
                </button>
              </div>

              <div style={styles.finaleLeaderboardHeaderRow}>
                {['#', 'Player', 'Score', 'Today', 'Thru'].map((label, i) => (
                  <span key={label} style={{ textAlign: i === 1 ? 'left' : 'center' }}>{label}</span>
                ))}
              </div>

              <div style={styles.finaleLeaderboardList}>
                {finaleLeaderboardLoading ? (
                  <div style={styles.finaleLeaderboardEmpty}>Loading leaderboard...</div>
                ) : finaleLeaderboardData.length === 0 ? (
                  <div style={styles.finaleLeaderboardEmpty}>🏌️ No scores yet</div>
                ) : (() => {
                  const participants = finaleLeaderboardData.filter(p => !p?.isNonParticipant);
                  const nonParticipants = finaleLeaderboardData.filter(p => p?.isNonParticipant);

                  const renderRow = (player, index) => {
                    const isCurrentUser = player?.userId === auth.currentUser?.uid;
                    const scoreColor = player?.isNonParticipant
                      ? '#4b5563'
                      : getScoreColor(player?.currentScore, player?.holesCompleted);
                    const todayColor = player?.isNonParticipant
                      ? '#4b5563'
                      : getScoreColor(player?.runningOverUnder, player?.holesCompleted);
                    const thru = player?.holesCompleted === 0 ? '' : player?.holesCompleted === 18 ? 'F' : String(player?.holesCompleted ?? '');

                    return (
                      <div
                        key={player?.userId}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '36px 1fr 52px 52px 36px',
                          gap: '8px',
                          alignItems: 'center',
                          padding: '10px 16px',
                          borderRadius: '6px',
                          marginBottom: '4px',
                          backgroundColor: player?.isNonParticipant ? '#0a0a0a' : (index % 2 === 0 ? '#0f0f0f' : '#1a1a1a'),
                          opacity: player?.isNonParticipant ? 0.4 : 1,
                          borderLeft: isCurrentUser ? '2px solid #d4af37' : '2px solid transparent',
                        }}
                      >
                        <div style={{ fontSize: '13px', color: player?.isNonParticipant ? '#4b5563' : '#9ca3af', textAlign: 'center' }}>
                          {player?.isNonParticipant ? '—' : (index < 3 ? ['🥇', '🥈', '🥉'][index] : `${index + 1}.`)}
                        </div>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: player?.isNonParticipant ? '#4b5563' : (isCurrentUser ? '#d4af37' : '#ffffff'),
                        }}>
                          {player?.name}
                        </div>
                        <div style={{ fontSize: '15px', fontWeight: '700', textAlign: 'center', color: scoreColor }}>
                          {formatFinaleScore(player?.currentScore)}
                        </div>
                        <div style={{ fontSize: '13px', fontWeight: '600', textAlign: 'center', color: todayColor }}>
                          {formatTodayScore(player?.runningOverUnder, player?.holesCompleted)}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280', textAlign: 'center' }}>
                          {thru}
                        </div>
                      </div>
                    );
                  };

                  return (
                    <>
                      {participants.map((player, index) => renderRow(player, index))}
                      {nonParticipants.length > 0 && (
                        <div style={{ fontSize: '11px', color: '#4b5563', padding: '6px 16px' }}>
                          Not participating
                        </div>
                      )}
                      {nonParticipants.map((player, index) => renderRow(player, index))}
                    </>
                  );
                })()}
              </div>

              <button onClick={loadFinaleLeaderboard} style={styles.finaleLeaderboardRefresh}>
                🔄 Refresh
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (step === 8) {
    const totals = calculateFinalTotals();

    return (
      <div style={styles.overlay}>
        <div style={styles.cardWide}>
          <h2 style={styles.title}>Round Summary</h2>
          <p style={styles.subtitle}>{selectedEvent.name} • {selectedCourse} • HCP: {handicap} • Course HCP: {courseHandicap}</p>

          <div style={styles.summaryGrid}>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Gross Score</div>
              <div style={styles.summaryValue}>{totals.totalGross}</div>
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Stableford Points</div>
              <div style={styles.summaryValue}>{totals.totalStableford}</div>
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Total Putts</div>
              <div style={styles.summaryValue}>{totals.totalPutts}</div>
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Fairways Hit</div>
              <div style={styles.summaryValue}>{totals.fairwaysHit}/{totals.fairwaysTotal} ({totals.fairwayPercentage}%)</div>
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Greens in Regulation</div>
              <div style={styles.summaryValue}>{totals.greensInRegulation}/{holes.length} ({totals.girPercentage}%)</div>
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Up & Down</div>
              <div style={styles.summaryValue}>{totals.upAndDowns}/{totals.upAndDownOpportunities} ({totals.upAndDownPercentage}%)</div>
            </div>
            {isFinale18 && (
              <>
                <div style={styles.summaryCard}>
                  <div style={styles.summaryLabel}>Teebox</div>
                  <div style={styles.summaryValue}>{getBergvlietTeeboxes()[selectedTeebox]?.label || selectedTeebox}</div>
                </div>
                <div style={styles.summaryCard}>
                  <div style={styles.summaryLabel}>Handicap Strokes</div>
                  <div style={styles.summaryValue}>-{totals.totalHandicapStrokes}</div>
                </div>
                <div style={styles.summaryCard}>
                  <div style={styles.summaryLabel}>Bonus Strokes</div>
                  <div style={styles.summaryValue}>-{finaleBonus}</div>
                </div>
                <div style={styles.summaryCard}>
                  <div style={styles.summaryLabel}>Final Net Score</div>
                  <div style={{
                    ...styles.summaryValue,
                    color: '#10b981',
                  }}>
                    {totals.totalGross - totals.totalHandicapStrokes - finaleBonus}
                  </div>
                </div>
              </>
            )}
          </div>

          <button onClick={() => setStep(8.5)} style={styles.nextButton}>
            Continue
          </button>
        </div>
      </div>
    );
  }

  if (step === 8.5) {
    const flightMembersData = approvedMembers.filter(m => flightMembers.includes(m.id));

    return (
      <div style={styles.overlay}>
        <div style={styles.card}>
          <h2 style={styles.title}>Select Your Marker</h2>
          <p style={styles.subtitle}>Who will verify your scorecard?</p>

          <div style={styles.membersList}>
            {flightMembersData.map(member => (
              <button
                key={member.id}
                onClick={() => handleMarkerSelect(member.id)}
                style={{
                  ...styles.memberCard,
                  ...(selectedMarker === member.id ? styles.memberCardSelected : {})
                }}
              >
                <div style={styles.memberInfo}>
                  <div style={styles.memberName}>
                    {selectedMarker === member.id && <span style={styles.checkmark}>✓ </span>}
                    {member.name}
                  </div>
                  {member.handicap && (
                    <div style={styles.memberHandicap}>HCP: {member.handicap}</div>
                  )}
                </div>
              </button>
            ))}
          </div>

          <div style={styles.buttonGroup}>
            <button onClick={() => setStep(8)} style={styles.backButton}>
              Back
            </button>
            <button onClick={handleMarkerSubmit} style={styles.nextButton}>
              Continue
            </button>
          </div>
        </div>
        {showModal && <MessageModal />}
      </div>
    );
  }

  if (step === 9) {
    return (
      <div style={styles.overlay}>
        <div style={styles.card}>
          <h2 style={styles.title}>Post-Round Questions</h2>
          <p style={styles.subtitle}>Almost done! Just a few fun stats...</p>

          <div style={styles.form}>
            <label style={styles.label}>
              Cigarettes smoked (required) 🚬
              <input
                type="number"
                value={cigarettesSmoked}
                onChange={(e) => setCigarettesSmoked(e.target.value)}
                placeholder="0"
                style={styles.input}
                min="0"
              />
            </label>

            <label style={styles.label}>
              Cigarettes borrowed from others (required) 🚬
              <input
                type="number"
                value={cigarettesBorrowed}
                onChange={(e) => setCigarettesBorrowed(e.target.value)}
                placeholder="0"
                style={styles.input}
                min="0"
              />
            </label>

          </div>

          <div style={styles.buttonGroup}>
            <button onClick={() => setStep(8.5)} style={styles.backButton}>
              Back
            </button>
            <button 
              onClick={handleFinalSubmit} 
              style={styles.submitButton}
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit Round 🎉'}
            </button>
          </div>
        </div>
        {showModal && <MessageModal />}

      {showAbortConfirm && (
  <div style={styles.confirmOverlay}>
    <div style={styles.confirmCard}>
      <h3 style={styles.confirmTitle}>🗑️ Delete Saved Round?</h3>
      <p style={styles.confirmText}>
        This will permanently delete your progress on this round. This cannot be undone.
      </p>
      <div style={styles.confirmButtons}>
        <button 
          onClick={() => setShowAbortConfirm(false)} 
          style={styles.confirmCancel}
        >
          Cancel
        </button>
        <button
          onClick={() => {
            deleteFinaleRoundDoc(finaleRoundDocId);
            localStorage.removeItem('golfRoundInProgress');
            setShowAbortConfirm(false);
            onClose();
          }}
          style={styles.confirmDelete}
        >
          Delete Round
        </button>
      </div>
    </div>
  </div>
)}
      </div>
    );

  }

  if (step === 10) {
    return (
      <div style={styles.overlay}>
        <div style={styles.card}>
          <div style={{ textAlign: 'center', marginBottom: '25px' }}>
            <div style={{ fontSize: '48px', marginBottom: '10px' }}>🎉</div>
            <h2 style={styles.title}>Round Submitted!</h2>
            <p style={styles.subtitle}>Awaiting approval from your marker.</p>
          </div>

          <div style={{ backgroundColor: '#0f0f0f', borderRadius: '12px', padding: '25px', border: '1px solid #2d2d2d', marginBottom: '25px' }}>
            <h3 style={styles.questionTitle}>🍺 Post-Round Refreshments</h3>
            <p style={{ color: '#9ca3af', textAlign: 'center', marginBottom: '25px', fontSize: '16px', fontWeight: '400' }}>
              How many beers did you have after your round?
            </p>
            <div style={styles.numberPad}>
              {[0,1,2,3,4,5,6,7,8,9,10].map(num => (
                <button
                  key={num}
                  onClick={() => handleBeerSubmit(num)}
                  style={styles.numberButton}
                >
                  {num}
                </button>
              ))}
              <button
                onClick={() => handleBeerSubmit(11)}
                style={styles.numberButton}
              >
                10+
              </button>
            </div>
          </div>

          <button
            onClick={() => handleBeerSubmit(null)}
            style={styles.skipButton}
          >
            Skip
          </button>
        </div>
      </div>
    );
  }
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    overflowY: 'auto',
    zIndex: 1000,
    animation: 'fadeIn 0.2s ease'
  },
  card: {
    backgroundColor: '#1e1e1e',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
    border: '1px solid #2d2d2d',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '95vh',
    overflowY: 'auto',
    animation: 'slideUp 0.25s ease'
  },
  cardWide: {
    backgroundColor: '#1e1e1e',
    padding: '40px',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
    border: '1px solid #2d2d2d',
    width: '100%',
    maxWidth: '900px',
    maxHeight: '85vh',
    overflowY: 'auto'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: '20px'
  },
  modalCard: {
    backgroundColor: '#1e1e1e',
    padding: '30px',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
    border: '1px solid #2d2d2d',
    maxWidth: '400px',
    width: '100%',
    textAlign: 'center'
  },
  modalTitle: {
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '15px',
    color: '#ffffff',
    letterSpacing: '-0.01em'
  },
  modalMessage: {
    fontSize: '16px',
    color: '#9ca3af',
    marginBottom: '20px',
    lineHeight: '1.5',
    fontWeight: '400'
  },
  modalButton: {
    padding: '12px 30px',
    backgroundColor: '#d4af37',
    color: '#0f0f0f',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    marginBottom: '10px',
    color: '#ffffff',
    letterSpacing: '-0.02em'
  },
  subtitle: {
    fontSize: '16px',
    color: '#9ca3af',
    marginBottom: '30px',
    fontWeight: '400'
  },
  emptyState: {
    textAlign: 'center',
    color: '#6b7280',
    padding: '40px 20px',
    fontSize: '16px',
    fontWeight: '400'
  },
  eventsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '25px',
    marginBottom: '20px'
  },
  eventSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  eventSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: '8px',
    borderBottom: '1px solid #2d2d2d'
  },
  sectionLabel: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#9ca3af',
    letterSpacing: '-0.01em'
  },
  todayBadge: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#10b981'
  },
  eventCount: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#6b7280',
    backgroundColor: '#0f0f0f',
    padding: '4px 10px',
    borderRadius: '12px'
  },
  eventsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  eventButton: {
    padding: '16px',
    backgroundColor: '#0f0f0f',
    border: '1px solid #2d2d2d',
    borderRadius: '8px',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.3s ease'
  },
  todayEventButton: {
    borderColor: '#10b981',
    backgroundColor: '#0f0f0f'
  },
  eventButtonDisabled: {
    backgroundColor: '#1a1a1a',
    border: '1px solid #2d2d2d',
    opacity: 0.5,
    cursor: 'not-allowed'
  },
  eventHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '6px',
    flexWrap: 'wrap',
    gap: '8px'
  },
  eventName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#ffffff',
    letterSpacing: '-0.01em'
  },
  finaleBadge: {
    backgroundColor: '#f59e0b',
    color: '#0f0f0f',
    padding: '3px 8px',
    borderRadius: '10px',
    fontSize: '10px',
    fontWeight: '700'
  },
  submittedBadge: {
    backgroundColor: '#10b981',
    color: '#0f0f0f',
    padding: '3px 8px',
    borderRadius: '10px',
    fontSize: '10px',
    fontWeight: '700'
  },
  eventDetails: {
    fontSize: '13px',
    color: '#9ca3af',
    marginBottom: '4px',
    fontWeight: '400'
  },
  eventFormat: {
    fontSize: '12px',
    color: '#6b7280',
    fontStyle: 'italic',
    fontWeight: '400'
  },
  moreEventsText: {
    fontSize: '13px',
    color: '#6b7280',
    textAlign: 'center',
    padding: '10px',
    fontStyle: 'italic'
  },
  courseButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    marginBottom: '20px'
  },
  courseButton: {
    padding: '20px',
    backgroundColor: '#d4af37',
    color: '#0f0f0f',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    textAlign: 'left',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  courseName: {
    fontSize: '20px',
    fontWeight: '700',
    marginBottom: '5px',
    letterSpacing: '-0.01em'
  },
  courseInfo: {
    fontSize: '14px',
    opacity: 0.9,
    fontWeight: '500'
  },
  resumeInfo: {
    backgroundColor: '#0f0f0f',
    padding: '20px',
    borderRadius: '8px',
    marginBottom: '30px',
    border: '1px solid #2d2d2d'
  },
  resumeDetail: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 0',
    borderBottom: '1px solid #2d2d2d'
  },
  resumeLabel: {
    fontSize: '14px',
    color: '#9ca3af',
    fontWeight: '500'
  },
  resumeValue: {
    fontSize: '14px',
    color: '#ffffff',
    fontWeight: '600'
  },
  resumeButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    marginBottom: '20px'
  },
  resumeButton: {
    width: '100%',
    padding: '18px',
    fontSize: '18px',
    fontWeight: '600',
    backgroundColor: '#10b981',
    color: '#0f0f0f',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  startNewButton: {
    width: '100%',
    padding: '18px',
    fontSize: '18px',
    fontWeight: '600',
    backgroundColor: '#d4af37',
    color: '#0f0f0f',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  membersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '20px',
    maxHeight: '400px',
    overflowY: 'auto'
  },
  memberCard: {
    padding: '15px',
    backgroundColor: '#0f0f0f',
    border: '1px solid #2d2d2d',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textAlign: 'left'
  },
  memberCardSelected: {
    backgroundColor: '#1e1e1e',
    borderColor: '#d4af37',
    boxShadow: '0 2px 8px rgba(212, 175, 55, 0.3)'
  },
  memberInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  memberName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#ffffff',
    letterSpacing: '-0.01em'
  },
  memberHandicap: {
    fontSize: '14px',
    color: '#9ca3af',
    fontWeight: '500'
  },
  checkmark: {
    color: '#d4af37',
    fontSize: '18px',
    marginRight: '5px'
  },
  selectionInfo: {
    textAlign: 'center',
    fontSize: '14px',
    color: '#9ca3af',
    marginBottom: '20px',
    fontWeight: '500'
  },
  totalsBar: {
    display: 'flex',
    justifyContent: 'space-around',
    backgroundColor: '#0f0f0f',
    padding: '10px',
    borderRadius: '12px',
    marginBottom: '10px',
    border: '1px solid #2d2d2d'
  },
  totalItem: {
    textAlign: 'center'
  },
  totalLabel: {
    fontSize: '12px',
    color: '#6b7280',
    marginBottom: '5px',
    textTransform: 'uppercase',
    fontWeight: '600',
    letterSpacing: '0.05em'
  },
  totalValue: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#d4af37',
    letterSpacing: '-0.02em'
  },
  progressBar: {
    width: '100%',
    height: '4px',
    backgroundColor: '#2d2d2d',
    borderRadius: '3px',
    overflow: 'hidden',
    marginBottom: '8px'
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#d4af37',
    transition: 'width 0.3s ease'
  },
  holeHeaderSection: {
    textAlign: 'center',
    marginBottom: '10px'
  },
  holeTitle: {
    fontSize: '26px',
    fontWeight: '700',
    color: '#ffffff',
    margin: '0 0 4px 0',
    letterSpacing: '-0.02em'
  },
  holeDetails: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
    fontSize: '14px',
    color: '#9ca3af',
    fontWeight: '500'
  },
  strokesPositive: {
    color: '#10b981',
    fontWeight: '700'
  },
  strokesNegative: {
    color: '#ef4444',
    fontWeight: '700'
  },
  questionSection: {
    marginBottom: '10px'
  },
  questionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#ffffff',
    textAlign: 'center',
    marginBottom: '12px',
    letterSpacing: '-0.01em'
  },
  numberPad: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '10px',
    marginBottom: '10px'
  },
  numberButton: {
    padding: '10px',
    fontSize: '22px',
    fontWeight: '700',
    backgroundColor: '#d4af37',
    color: '#0f0f0f',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  yesNoButtons: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    marginBottom: '10px'
  },
  yesButton: {
    padding: '20px',
    fontSize: '20px',
    fontWeight: '700',
    backgroundColor: '#10b981',
    color: '#0f0f0f',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  noButton: {
    padding: '20px',
    fontSize: '20px',
    fontWeight: '700',
    backgroundColor: '#ef4444',
    color: '#ffffff',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  skipButton: {
    width: '100%',
    padding: '15px',
    fontSize: '16px',
    backgroundColor: '#2d2d2d',
    color: '#9ca3af',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  holeNavigation: {
    marginBottom: '8px'
  },
  prevHoleButton: {
    width: '100%',
    padding: '10px',
    fontSize: '14px',
    fontWeight: '600',
    backgroundColor: '#6b7280',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  saveExitButton: {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    fontWeight: '600',
    backgroundColor: '#f59e0b',
    color: '#0f0f0f',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '1px solid #2d2d2d',
    borderRadius: '8px',
    marginTop: '8px',
    boxSizing: 'border-box',
    backgroundColor: '#0f0f0f',
    color: '#ffffff',
    fontWeight: '500'
  },
  infoBox: {
    backgroundColor: '#0f0f0f',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '16px',
    color: '#9ca3af',
    textAlign: 'center',
    border: '1px solid #2d2d2d',
    fontWeight: '500'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    marginBottom: '20px'
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    fontSize: '16px',
    fontWeight: '600',
    color: '#ffffff'
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '15px',
    marginBottom: '30px'
  },
  summaryCard: {
    backgroundColor: '#0f0f0f',
    padding: '20px',
    borderRadius: '8px',
    textAlign: 'center',
    border: '1px solid #2d2d2d'
  },
  summaryLabel: {
    fontSize: '14px',
    color: '#6b7280',
    marginBottom: '8px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  summaryValue: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#d4af37',
    letterSpacing: '-0.02em'
  },
  buttonGroup: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'space-between'
  },
  backButton: {
    padding: '12px 24px',
    backgroundColor: '#2d2d2d',
    color: '#9ca3af',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  abortButton: {
    padding: '15px 20px',
    backgroundColor: '#ef4444',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
    marginTop: '10px',
    transition: 'all 0.3s ease'
  },
  nextButton: {
    width: '100%',
    padding: '15px',
    backgroundColor: '#d4af37',
    color: '#0f0f0f',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '18px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  submitButton: {
    padding: '12px 24px',
    backgroundColor: '#10b981',
    color: '#0f0f0f',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    flex: 1,
    transition: 'all 0.3s ease'
  },
  cancelButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#2d2d2d',
    color: '#9ca3af',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  confirmOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000
  },
  confirmCard: {
    backgroundColor: '#1e1e1e',
    borderRadius: '12px',
    padding: '30px',
    maxWidth: '400px',
    margin: '20px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
    border: '1px solid #2d2d2d'
  },
  confirmTitle: {
    fontSize: '22px',
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: '15px',
    textAlign: 'center',
    letterSpacing: '-0.01em'
  },
  confirmText: {
    fontSize: '15px',
    color: '#9ca3af',
    marginBottom: '25px',
    lineHeight: '1.6',
    textAlign: 'center',
    fontWeight: '400'
  },
  confirmButtons: {
    display: 'flex',
    gap: '15px'
  },
  confirmCancel: {
    flex: 1,
    padding: '12px',
    backgroundColor: '#2d2d2d',
    color: '#9ca3af',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  confirmDelete: {
    flex: 1,
    padding: '12px',
    backgroundColor: '#ef4444',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  finaleLeaderboardButton: {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    fontWeight: '600',
    backgroundColor: 'transparent',
    color: '#d4af37',
    border: '1px solid #d4af37',
    borderRadius: '8px',
    cursor: 'pointer',
    marginTop: '8px',
    transition: 'all 0.3s ease'
  },
  finaleLeaderboardOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.9)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  },
  finaleLeaderboardCard: {
    backgroundColor: '#1e1e1e',
    borderRadius: '12px',
    border: '1px solid #2d2d2d',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
    overflow: 'hidden'
  },
  finaleLeaderboardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px'
  },
  finaleLeaderboardTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#ffffff',
    margin: 0,
    letterSpacing: '-0.01em'
  },
  finaleLeaderboardSubtitle: {
    fontSize: '13px',
    color: '#9ca3af',
    margin: '4px 0 0 0',
    fontWeight: '400'
  },
  finaleLeaderboardClose: {
    background: 'transparent',
    border: 'none',
    color: '#9ca3af',
    fontSize: '20px',
    cursor: 'pointer',
    padding: '0 0 0 12px'
  },
  finaleLeaderboardHeaderRow: {
    display: 'grid',
    gridTemplateColumns: '36px 1fr 52px 52px 36px',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: '#0f0f0f',
    borderRadius: '8px 8px 0 0',
    color: '#6b7280',
    fontSize: '10px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  finaleLeaderboardList: {
    overflowY: 'auto',
    flex: 1
  },
  finaleLeaderboardEmpty: {
    color: '#6b7280',
    fontSize: '14px',
    textAlign: 'center',
    padding: '30px'
  },
  finaleLeaderboardRefresh: {
    backgroundColor: 'transparent',
    color: '#9ca3af',
    border: '1px solid #2d2d2d',
    borderRadius: '6px',
    padding: '8px 20px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
    marginTop: '12px'
  },
};

export default ScoreEntry;